'use client';

import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '../Icon';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { MdChevronRight } from 'react-icons/md';

type StepsDirection = 'horizontal' | 'vertical';
type StepsSize = 'default' | 'small';

export interface StepsProps {
  current: number;
  size?: StepsSize;
  direction?: StepsDirection;
  steps: { title: string }[];
  variant?: string;
}

interface StepProps {
  current: number;
  index: number;
  lastStep: boolean;
  title: string;
  variant?: string;
}

export const Steps: React.FC<StepsProps> = ({ current, direction = 'horizontal', size, steps, variant }) => {
  return (
    <Container className={`steps steps-${direction} steps-${size}`}>
      {steps.map((step, index) => {
        return (
          <Step
            current={current}
            index={index}
            key={step.title}
            lastStep={index === steps.length - 1}
            title={step.title}
            variant={variant}
          />
        );
      })}
    </Container>
  );
};

export const Step = ({ current, index, lastStep, title, variant }: StepProps) => {
  const currentItem = index + 1;
  const isFinished = currentItem < current + 1;
  const isActive = currentItem === current + 1;
  return (
    <div className={`steps-item ${isFinished ? 'steps-item-finished' : isActive ? 'steps-item-active' : ''}`}>
      <div className="steps-item-container flex flex-row">
        <div>
          <div className="steps-item-tail"></div>
          <div className="steps-item-icon-wrapper">
            {isFinished ? (
              <Icon className="steps-item-icon check-mark-icon" icon={faCheck} />
            ) : (
              <span className="steps-item-icon">{currentItem}</span>
            )}
          </div>
          <div className="steps-item-content">
            <div className={`steps-item-title ${variant === 'arrow' ? 'arrow-variant' : ''}`}>{title}</div>
          </div>
        </div>
        {!lastStep && variant === 'arrow' && (
          <div className="flex-grow hidden md:flex justify-center items-center">
            <MdChevronRight className="w-6 h-6 text-slate-400" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Steps;

const Container = styled.div`
  &.steps {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    font-variant: tabular-nums;
    line-height: 1.5715;
    list-style: none;
    font-feature-settings: 'tnum';
    display: flex;
    width: 100%;
    font-size: 0;
    text-align: initial;

    .steps-item {
      position: relative;
      display: inline-block;
      flex: 1;
      overflow: hidden;
      vertical-align: top;
      &:last-child {
        flex: none;
      }
    }

    .steps-item-icon-wrapper,
    .steps-item-content {
      display: inline-block;
      vertical-align: top;
    }
    .steps-item-icon-wrapper {
      width: 32px;
      height: 32px;
      margin: 0 8px 0 0;
      font-size: 16px;
      line-height: 32px;
      text-align: center;
      border: 1px solid ${({ theme }) => theme.colorPalette.gray300};
      color: ${({ theme }) => theme.colorPalette.gray300};
      border-radius: ${({ theme }) => theme.borderRadius.full};
      transition:
        background-color 0.3s,
        border-color 0.3s;
      margin-left: 1rem;
      .steps-item-icon {
        display: inline-block;
        position: relative;
        top: -0.5px;
        color: ${({ theme }) => theme.colorPalette.blue500};
        line-height: 1;

        &.check-mark-icon {
          font-size: ${({ theme }) => theme.fontSize.sm};
        }
      }
    }
    .steps-item-tail {
      position: absolute;
      top: 12px;
      left: 0;
      width: 100%;
      padding: 0 10px;
      display: none;

      &:after {
        display: inline-block;
        width: 100%;
        height: 1px;
        background: ${({ theme }) => theme.colorPalette.gray300};
        border-radius: 1px;
        transition: background 0.3s;
        content: '';
      }
    }

    .steps-item-title {
      position: relative;
      display: inline-block;
      padding-right: 1rem;
      color: rgba(0, 0, 0, 0.85);
      font-size: 16px;
      line-height: 32px;
      &:after {
        position: absolute;
        top: 16px;
        left: 100%;
        display: block;
        width: 9999px;
        height: 1px;
        background-color: ${({ theme }) => theme.colorPalette.gray300};
        content: '';
      }
      &.arrow-variant:after {
        display: none;
      }
    }

    .steps-item-icon-wrapper .steps-item-icon {
      color: ${({ theme }) => theme.colorPalette.gray300};
    }

    .steps-item-active .steps-item-icon-wrapper {
      background-color: ${({ theme }) => theme.colorPalette.blue500};
      border-color: ${({ theme }) => theme.colorPalette.blue500};
      color: ${({ theme }) => theme.colorPalette.white};

      .steps-item-icon {
        color: ${({ theme }) => theme.colorPalette.white};
      }
    }

    .steps-item-finished {
      .steps-item-icon {
        width: 12px;
        height: 12px;
      }
      .steps-item-icon-wrapper {
        background-color: #fff;
        border-color: ${({ theme }) => theme.colorPalette.blue500};
        top: unset;

        svg {
          color: ${({ theme }) => theme.colorPalette.blue500};
        }
      }
    }

    .steps-item-finished {
      .steps-item-tail,
      .steps-item-title {
        &:after {
          background-color: ${({ theme }) => theme.colorPalette.blue500};
        }
      }
    }
    .steps-small {
      .steps-item-icon-wrapper {
        width: 24px;
        height: 24px;
        margin: 0 8px 0 0;
        font-size: 12px;
        line-height: 24px;
        text-align: center;
        border-radius: 24px;
      }
      .steps-item-title {
        font-size: 14px;
        line-height: 24px;
        &:after {
          top: 12px;
        }
      }
    }
    .steps-small .steps-item-tail {
      top: 8px;
    }
    &.steps-vertical {
      display: flex;
      flex-direction: column;
      .steps-item {
        display: block;
        flex: 1 0 auto;
        padding-left: 0;
        overflow: visible;
      }
      .steps-item .steps-item-icon-wrapper {
        float: left;
        margin-left: 0;
        left: 0;
      }
      .steps-item .steps-item-content {
        display: block;
        min-height: 48px;
        overflow: hidden;
        position: relative;
        top: 4px;
      }
      .steps-item-title {
        line-height: 32px;
      }
      .steps-item-tail {
        position: absolute;
        top: 4px;
        left: 16px;
        width: 1px;
        height: 100%;
        padding: 38px 0 6px;

        &:after {
          width: 1px;
          height: 100%;
        }
      }
      .steps-item {
        &:not(:last-child) {
          .steps-item-container {
            .steps-item-tail {
              display: block;
            }
          }
        }
      }
      .steps-item > .steps-item-container > .steps-item-content > .steps-item-title::after {
        display: none;
      }
      &.steps-small .steps-item-container .steps-item-tail {
        position: absolute;
        padding: 30px 0 6px;
      }
      &.steps-small .steps-item-container .steps-item-title {
        line-height: 24px;
      }
    }
    @media (max-width: 600px) {
      display: flex;
      flex-direction: column;
      .steps-item {
        display: block;
        flex: 1 0 auto;
        padding-left: 0;
        overflow: visible;
      }
      .steps-item .steps-item-icon-wrapper {
        float: left;
        margin-left: 0;
        left: 0;
      }
      .steps-item .steps-item-content {
        display: block;
        min-height: 48px;
        overflow: hidden;
        position: relative;
        top: 4px;
      }
      .steps-item-title {
        line-height: 32px;
      }
      .steps-item-tail {
        position: absolute;
        top: 4px;
        left: 16px;
        width: 1px;
        height: 100%;
        padding: 38px 0 6px;

        &:after {
          width: 1px;
          height: 100%;
        }
      }
      .steps-item {
        &:not(:last-child) {
          .steps-item-container {
            .steps-item-tail {
              display: block;
            }
          }
        }
      }
      .steps-item > .steps-item-container > .steps-item-content > .steps-item-title::after {
        display: none;
      }
      &.steps-small .steps-item-container .steps-item-tail {
        position: absolute;
        padding: 30px 0 6px;
      }
      &.steps-small .steps-item-container .steps-item-title {
        line-height: 24px;
      }
    }
  }
`;
